import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify"; // Add these imports
import "react-toastify/dist/ReactToastify.css"; // Add these imports
import "./send_notification.scss";
import logo from "../../assets/fitoja-logo.jpg";
import maleProfilePic from "../../assets/profile_pic_male.jpeg";
import closeIcon from "../../assets/close_icon.png";
import ReactQuill from "react-quill";

interface User {
  _id: string;
  name: string;
  phoneNumber: string;
  email: string;
  gender: string;
  age: string;
  weight: string;
  height: string;
  goal: string;
  activity: string;
  medicalConcern: string;
  startingBmi: string;
  languageCode: string;
  profilePicture: string;
  activePlans: any;
  historyPlans: any;
  weightEntries: object;
  dietPlan: object;
  createdAt: Date;
  updatedAt: Date;
  oneSignalPlayerId: string;
}

const SendNotification = () => {
  const [userDetails, setUserDetails] = useState<any>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [role, setRole] = useState<string>("");
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [sendNotificationModal, setSendNotificationModal] =
    useState<boolean>(false);
  const [sendNotificationToAllModal, setSendNotificationToAllModal] =
    useState<boolean>(false);
  const [expandedUsers, setExpandedUsers] = useState<{
    [key: string]: boolean;
  }>({});
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<string>("");
  const [notificationTitle, setNotificationTitle] = useState<string>("");
  const [notificationMessage, setNotificationMessage] = useState<string>("");
  const [userPlayerId, setUserPlayerId] = useState<string>("");
  const navigate = useNavigate();
  const isInitialRender = useRef(true);
  const [oneSignalPlayerIds, setOneSignalPlayerIds] = useState([]);

  const fetchUserDetails = async () => {
    try {
      const email = localStorage.getItem("email");
      const token = localStorage.getItem("token");
      if (!email || !token) {
        alert("Authentication failed! Redirecting to login.");
        navigate("/login");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(
        `https://api.fitoja.in/api/get-employee-details/${email}`,
        config
      );
      setUserDetails(response.data);
      setRole(response.data.role);
      fetchAllUsers();
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      alert("Session expired or invalid token! Redirecting to login.");
      handleLogout();
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(
        `https://api.fitoja.in/api/get-all-users-info`
      );
      const usersData = response.data.data;

      setUsers(usersData);

      // 👇 Step 2: Extract all available oneSignalPlayerIds
      const ids = usersData
        .filter((user) => user.oneSignalPlayerId) // only include users with the field
        .map((user) => user.oneSignalPlayerId);

      setOneSignalPlayerIds(ids); // 👈 Step 3: Store in state
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("email");
    localStorage.removeItem("token");
    navigate("/login");
  };

  const toggleExpanded = (userId: string) => {
    setExpandedUsers((prev) => ({ ...prev, [userId]: !prev[userId] }));
  };

  useEffect(() => {
    if (isInitialRender.current) {
      fetchUserDetails();
      isInitialRender.current = false;
    }
  }, []);

  const filteredUsers = users.filter((user) =>
    user.phoneNumber.includes(searchTerm)
  );

  // Updated sendNotification function with toaster
  const sendNotification = async () => {
    try {
      const data = {
        phoneNumber: selectedPhoneNumber,
        title: notificationTitle,
        description: notificationMessage,
        isViewed: false,
        isSubscriptionAlert: false,
      };

      const response = await axios.post(
        "https://api.fitoja.in/api/create-notification",
        data
      );

      // Show success toaster
      toast.success("Notification sent successfully!", {
        position: "top-right",
        autoClose: 7000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      // Clear inputs and close modal
      setNotificationTitle("");
      setNotificationMessage("");
      setSendNotificationModal(false);
    } catch (error) {
      console.error("Failed to send notification:", error);
      toast.error("Failed to send notification.", {
        position: "top-right",
        autoClose: 7000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
      });
    }
  };

  function stripHtml(html) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  }

  const sendPushNotification = async (playerId) => {
    const response = await axios.post(
      "https://onesignal.com/api/v1/notifications",
      {
        app_id: "18b90dfb-da22-4dbe-b507-95930f99fe9a",
        include_player_ids: [playerId],
        headings: { en: notificationTitle },
        contents: { en: stripHtml(notificationMessage) },
        large_icon: "https://fitoja.in/static/media/fitoja-logo.8b718acb8b97f5f8f5fc.png",
        // small_icon: "https://fitoja.in/static/media/fitoja-logo.8b718acb8b97f5f8f5fc.png",
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization:
            "Basic os_v2_app_dc4q3662ejg35nihswjq7gp6tiwo7kz44ateraul4cl7hqkha6mtkhjcfl4srw7pk3imzs5cssaqqkxzrxjvh5xi3n65pq6ln4fqeca",
        },
      }
    );
    console.log(response.data);
  };

  const sendPushNotificationToAll = async () => {
    const response = await axios.post(
      "https://onesignal.com/api/v1/notifications",
      {
        app_id: "18b90dfb-da22-4dbe-b507-95930f99fe9a",
        include_player_ids: oneSignalPlayerIds,
        headings: { en: notificationTitle },
        contents: { en: stripHtml(notificationMessage) },
        large_icon: "https://fitoja.in/static/media/fitoja-logo.8b718acb8b97f5f8f5fc.png",
        // small_icon: "https://fitoja.in/static/media/fitoja-logo.8b718acb8b97f5f8f5fc.png",
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization:
            "Basic os_v2_app_dc4q3662ejg35nihswjq7gp6tiwo7kz44ateraul4cl7hqkha6mtkhjcfl4srw7pk3imzs5cssaqqkxzrxjvh5xi3n65pq6ln4fqeca",
        },
      }
    );

    console.log(response.data);
  };

  return (
    <div className="send-notification">
      <ToastContainer theme="colored" /> {/* Toaster container */}
      {loading && (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading, please wait...</p>
        </div>
      )}
      {!loading && (
        <>
          <header className="dashboard-header">
            <div className="logo" onClick={() => navigate("/dashboard")}>
              <img src={logo} alt="Logo" />
            </div>
            <div
              className="profile-section"
              onClick={() => setShowOverlay(!showOverlay)}
            >
              <img className="profile-pic" src={maleProfilePic} alt="Profile" />
            </div>
          </header>

          {showOverlay && userDetails && (
            <div className="profile-overlay">
              <div className="overlay-content">
                <img
                  className="close_icon"
                  onClick={() => setShowOverlay(false)}
                  src={closeIcon}
                  alt="Close"
                />
                <p>
                  <strong>Name:</strong> {userDetails.name}
                </p>
                <p>
                  <strong>Email:</strong> {userDetails.email}
                </p>
                <p>
                  <strong>Phone Number:</strong> {userDetails.phoneNumber}
                </p>
                <p>
                  <strong>Role:</strong> {userDetails.role}
                </p>
                <p>
                  <strong>Gender:</strong> {userDetails.gender}
                </p>
                <button className="logout-button" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          )}

          <div className="search-box">
            <h2>Send Notification to any user</h2>
            <input
              type="text"
              placeholder="Search user by Phone Number"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <button
            className="send-notification-btn"
            style={{
              padding: "2px 10px",
              background: "#4caf50",
              borderRadius: "8px",
              marginTop: "10px",
              fontWeight: "600",
              fontSize: "20px",
              border: "none",
              color: "white",
              height: "min-content",
              marginLeft: "auto",
              marginRight: "15px",
            }}
            onClick={() => {
              setSendNotificationToAllModal(true);
            }}
          >
            Send Notification to All the users
          </button>

          <div className="user-list">
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <div key={user._id} className="user-card">
                  <div className="user-details">
                    <p>
                      <strong>Name:</strong> <span>{user.name}</span>
                    </p>
                    <p>
                      <strong>Phone Number:</strong>{" "}
                      <span>{user.phoneNumber}</span>
                    </p>
                    <p>
                      <strong>Email:</strong> <span>{user.email}</span>
                    </p>
                    <p>
                      <strong>Gender:</strong> <span>{user.gender}</span>
                    </p>
                    <p>
                      <strong>Age:</strong> <span>{user.age}</span>
                    </p>
                    <p>
                      <strong>Height:</strong> <span>{user.height}</span>
                    </p>
                    <p>
                      <strong>Weight:</strong> <span>{user.weight}</span>
                    </p>
                  </div>
                  <button
                    className="send-notification-btn"
                    onClick={() => {
                      setSelectedPhoneNumber(user.phoneNumber);
                      setUserPlayerId(user.oneSignalPlayerId);
                      setSendNotificationModal(true);
                    }}
                  >
                    Send Notification
                  </button>
                </div>
              ))
            ) : (
              <p>No User found.</p>
            )}
          </div>

          {sendNotificationModal && (
            <div className="send-notification-modal">
              <div className="send-notification-modal-content">
                <h2>Send Notification</h2>
                <span
                  style={{
                    alignItems: "baseline",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <label>Title</label>
                  <input
                    style={{
                      width: "100%",
                      borderRadius: "4px",
                      border: "1px solid grey",
                      padding: "10px",
                    }}
                    placeholder="Enter Notification Title"
                    value={notificationTitle}
                    onChange={(e) => setNotificationTitle(e.target.value)}
                  />
                </span>
                <span
                  style={{
                    alignItems: "baseline",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <label>Message</label>
                  <ReactQuill
                    style={{ width: "100%" }}
                    value={notificationMessage}
                    onChange={(value) => setNotificationMessage(value)}
                    modules={{
                      toolbar: [
                        [{ header: [1, 2, 3, 4, 5, 6, false] }],
                        ["bold", "italic", "underline"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        [{ align: [] }],
                        ["image"],
                        ["clean"], // Remove formatting
                      ],
                    }}
                    formats={[
                      "header",
                      "bold",
                      "italic",
                      "underline",
                      "blockquote",
                      "code-block",
                      "list",
                      "bullet",
                      "align",
                      "link",
                      "image",
                    ]}
                  />
                </span>
                <div style={{ display: "grid" }}>
                  <button
                    onClick={() => {
                      sendNotification();
                      if (userPlayerId != null) {
                        sendPushNotification(userPlayerId);
                      }
                    }}
                    className="send-button"
                  >
                    Send
                  </button>
                  <button
                    onClick={() => {
                      setSendNotificationModal(false);
                      setNotificationTitle("");
                      setNotificationMessage("");
                    }}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {sendNotificationToAllModal && (
            <div className="send-notification-modal">
              <div className="send-notification-modal-content">
                <h2>Send Notification to Al Users</h2>
                <span
                  style={{
                    alignItems: "baseline",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <label>Title</label>
                  <input
                    style={{
                      width: "100%",
                      borderRadius: "4px",
                      border: "1px solid grey",
                      padding: "10px",
                    }}
                    placeholder="Enter Notification Title"
                    value={notificationTitle}
                    onChange={(e) => setNotificationTitle(e.target.value)}
                  />
                </span>
                <span
                  style={{
                    alignItems: "baseline",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <label>Message</label>
                  <ReactQuill
                    style={{ width: "100%" }}
                    value={notificationMessage}
                    onChange={(value) => setNotificationMessage(value)}
                    modules={{
                      toolbar: [
                        [{ header: [1, 2, 3, 4, 5, 6, false] }],
                        ["bold", "italic", "underline"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        [{ align: [] }],
                        ["image"],
                        ["clean"], // Remove formatting
                      ],
                    }}
                    formats={[
                      "header",
                      "bold",
                      "italic",
                      "underline",
                      "blockquote",
                      "code-block",
                      "list",
                      "bullet",
                      "align",
                      "link",
                      "image",
                    ]}
                  />
                </span>
                <div style={{ display: "grid" }}>
                  <button
                    onClick={() => {
                      sendPushNotificationToAll();
                    }}
                    className="send-button"
                  >
                    Send
                  </button>
                  <button
                    onClick={() => {
                      setSendNotificationToAllModal(false);
                      setNotificationTitle("");
                      setNotificationMessage("");
                    }}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SendNotification;
